import {
  AuthFields,
  AuthHeading,
  AuthNote,
  AuthShell,
  AuthStack,
  authActionClass,
  Button,
  PasswordField,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, type ReactNode, useState } from "react";
import { type Problem, passwordProblem, setPasswordProblem } from "../auth-copy";
import { landingFor } from "../guards";
import { isAuthError, meQuery, setPassword, signIn } from "../session";
import { HELP_LINE } from "./sign-in";

const EXPIRED_NOTE =
  "The link in your invitation works for 72 hours. If it has expired, ask an administrator to resend it.";
const RESET_EXPIRED_NOTE =
  "The link works for an hour. If it has expired, ask for a new one with Forgot your password on the sign-in page.";

/**
 * First sign-in from an invitation (`DpZf5`): /set-password?token=…&email=… . With the email
 * (invitations carry it), the person is signed in straight away; without it, they go to sign in.
 * With reset=1 it is a forgotten password's new one, from the reset email: their other sessions end.
 */
export function SetPasswordPage(): ReactNode {
  const { token, email, reset } = useSearch({ from: "/set-password" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPasswordValue] = useState("");
  const [confirm, setConfirm] = useState("");
  const [problem, setProblem] = useState<Problem>({});
  const [confirmError, setConfirmError] = useState<string>();
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) return;
    const bad = passwordProblem(password);
    const mismatch = !bad && confirm !== password ? "The two passwords don't match." : undefined;
    setProblem(bad ? { password: bad } : {});
    setConfirmError(mismatch);
    if (bad || mismatch) return;

    setPending(true);
    try {
      await setPassword(token, password);
    } catch (error) {
      setProblem(
        setPasswordProblem(isAuthError(error) ? error.code : "UNKNOWN", isAuthError(error) ? error.message : "", reset),
      );
      setPending(false);
      return;
    }
    if (email) {
      try {
        await signIn(email, password);
        const me = await queryClient.fetchQuery({ ...meQuery, staleTime: 0 });
        await navigate({ href: landingFor(me) });
        return;
      } catch {
        // the password is set; signing in by hand is the fallback
      }
    }
    await navigate({ to: "/sign-in", search: { notice: "password-set" } });
  };

  if (!token) {
    return (
      <AuthShell product="Admin console" helpLine={HELP_LINE}>
        <AuthStack>
          <AuthHeading title={reset ? "Choose a new password" : "Set your password"} />
          <Alert tone="danger" title="This link is incomplete">
            {reset
              ? "Open the link in the reset email again. If it still doesn't work, ask for a new one with Forgot your password."
              : "Open the link in your invitation email again. If it still doesn't work, ask an administrator to resend your invitation."}
          </Alert>
        </AuthStack>
      </AuthShell>
    );
  }

  return (
    <AuthShell product="Admin console" helpLine={HELP_LINE}>
      <form onSubmit={submit} noValidate>
        <AuthStack>
          <AuthHeading
            title={reset ? "Choose a new password" : "Set your password"}
            description={
              reset
                ? "Choose the password you'll use from now on. You'll be signed out everywhere else."
                : "Welcome to MajiTap. Choose the password you'll use to sign in to the console."
            }
          />
          {problem.alert ? (
            <Alert tone="danger" title={problem.alert.title}>
              {problem.alert.body}
            </Alert>
          ) : null}
          <AuthFields>
            <PasswordField
              label="New password"
              name="new-password"
              autoComplete="new-password"
              description="At least 8 characters, not only digits. Staff can't use a 4-digit PIN."
              error={problem.password}
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
            />
            <PasswordField
              label="Confirm password"
              name="confirm-password"
              autoComplete="new-password"
              description="Type it again."
              error={confirmError}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </AuthFields>
          <Button type="submit" variant="primary" className={authActionClass} loading={pending}>
            Set password and sign in
          </Button>
          <AuthNote>{reset ? RESET_EXPIRED_NOTE : EXPIRED_NOTE}</AuthNote>
        </AuthStack>
      </form>
    </AuthShell>
  );
}
